import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { realAPI } from "../../services/realAPI";
import { Task } from "../../types/types";
import StoryModal from "../StoryModal/StoryModal";
import Header from "../Header/Header";
import { Menu, X, Calendar } from "lucide-react";

interface Team {
  id: string;
  name: string;
  description: string;
  memberIds: string[];
  enabled?: boolean;
}

const TaskPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sprints, setSprints] = useState<any[]>([]);
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [userTeams, setUserTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    // If not authenticated, redirect to login with return URL
    if (!accessToken) {
      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      navigate(`/login?redirect=${encodeURIComponent(returnTo)}`, {
        replace: true,
      });
      return;
    }

    const fetchTask = async () => {
      if (!id) {
        setError("Missing task id");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const t = await realAPI.getTaskById(id);
        setTask(t);
      } catch (e: any) {
        setError(e?.message || "Failed to load task");
      } finally {
        setLoading(false);
      }
    };
    fetchTask();
  }, [id, navigate, location.pathname, location.search, location.hash]);

  // Load current user from localStorage and fetch teams
  useEffect(() => {
    try {
      const currentUserString = localStorage.getItem("currentUser");
      if (currentUserString) {
        const user = JSON.parse(currentUserString);
        setCurrentUser(user);
        if (user?.organizationId) {
          realAPI
            .getTeamsByOrganization(user.organizationId)
            .then((teams: Team[]) => setUserTeams(teams))
            .catch(() => {});
        }
      }
    } catch (err) {
      // ignore
    }
  }, []);

  // When teams or task change, select team for context
  useEffect(() => {
    if (task?.teamId && userTeams.length) {
      const team =
        userTeams.find((t) => String(t.id) === String(task.teamId)) || null;
      setSelectedTeam(team);
    }
  }, [task?.teamId, userTeams]);

  // Load sprints for sidebar using org/team context from localStorage
  useEffect(() => {
    const loadSprints = async () => {
      try {
        // Prefer teamId from the task once loaded; fallback to organization
        const teamId = task?.teamId || localStorage.getItem("currentTeamId");
        if (teamId) {
          const list = await realAPI.getSprintsByTeam(String(teamId));
          setSprints(list || []);
        } else {
          const org = localStorage.getItem("organizationId");
          if (org) {
            const list = await realAPI.getSprintsByOrganization(org);
            setSprints(list || []);
          }
        }
      } catch (e) {
        // Non-fatal for the page
        console.warn("Failed to load sprints", e);
      }
    };
    loadSprints();
  }, [task?.teamId]);

  const handleClose = () => {
    // Try to go back, otherwise go home
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("currentUser");
      localStorage.removeItem("organizationData");
    } finally {
      // Force reload to reset App state
      window.location.href = "/";
    }
  };

  // Navigate back to dashboard with desired tab (backlog or sprint id)
  const goToDashboard = (tab?: string) => {
    const params = new URLSearchParams();
    if (tab) params.set("tab", tab);
    if (task?.teamId) params.set("team", String(task.teamId));
    const query = params.toString();
    const url = `/${query ? `?${query}` : ""}`;
    console.log(`🚀 TaskPage navigating to dashboard: ${url}`, {
      tab,
      teamId: task?.teamId,
    });
    navigate(url);
  };

  // Helper: format date range for sprint chip
  const formatDate = (date: string | Date | undefined) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const sprintForTask = React.useMemo(() => {
    const sId =
      task?.status && task.status !== "backlog" ? String(task.status) : null;
    if (!sId) return null;
    return sprints.find((s: any) => String(s.id) === sId) || null;
  }, [task?.status, sprints]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Loading task…
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-6">
        <h1 className="text-xl font-semibold text-red-600 mb-2">
          Unable to open task
        </h1>
        <p className="text-gray-600 mb-4">{error || "Task not found."}</p>
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-md"
          onClick={() => navigate("/")}
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      {currentUser ? (
        <header className="bg-white shadow-sm">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <svg
                  className="w-8 h-8 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 17v-2m3 2v-4m3 4v-6m-9 4h12M3 7h18M5 11h14M5 15h14M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z"
                  ></path>
                </svg>
                <h1 className="text-xl font-bold text-gray-800">
                  Kanban Board
                </h1>
              </div>
              <div className="flex items-center space-x-4">
                <span className="hidden sm:block text-sm text-gray-600">
                  Welcome, {currentUser.firstName} {currentUser.lastName}!
                </span>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 text-gray-600 hover:text-indigo-600 transition-colors"
                  title="Logout"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    ></path>
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            </div>

            {/* Organization chip */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {currentUser?.organization?.name && (
                <span className="px-2 md:px-3 py-1 bg-indigo-100 text-indigo-800 text-xs md:text-sm font-medium rounded-full">
                  {currentUser.organization.name}
                </span>
              )}
            </div>
          </div>
        </header>
      ) : (
        <Header />
      )}

      {/* Team Context Info */}
      {currentUser && selectedTeam && (
        <div className="bg-indigo-50 border-b border-indigo-200">
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-indigo-600 font-medium">
                  Current Team:
                </span>
                <span className="text-indigo-800">{selectedTeam.name}</span>
                <span className="text-indigo-500">•</span>
                <span className="text-indigo-600">
                  {selectedTeam.description}
                </span>
                <span className="text-indigo-500">•</span>
                <span className="text-indigo-600">
                  {selectedTeam.memberIds.length} members
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex flex-1">
        {/* Left sidebar (hidden on small screens) */}
        <aside className="hidden md:block w-64 border-r border-gray-200 bg-gray-50 p-4 space-y-4">
          <div>
            <h2 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              Backlog
            </h2>
            <button
              className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-100"
              onClick={() => goToDashboard("backlog")}
            >
              Backlog
            </button>
          </div>
          <div>
            <h2 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              Sprints
            </h2>
            <div className="space-y-1">
              {sprints.map((s: any) => (
                <button
                  key={s.id}
                  className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-100"
                  onClick={() => goToDashboard(String(s.id))}
                  title={s.description}
                >
                  {s.name}
                  {s.status ? (
                    <span className="ml-2 text-xs text-gray-500">
                      ({s.status})
                    </span>
                  ) : null}
                </button>
              ))}
              {sprints.length === 0 && (
                <div className="text-xs text-gray-400 px-3">No sprints</div>
              )}
            </div>
          </div>
        </aside>

        {/* Main content: embedded story */}
        <main className="flex-1 p-4 sm:p-6">
          {/* Mobile: open sidebar button */}
          <div className="md:hidden mb-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">Task #{String(task.id)}</div>
            <button
              className="inline-flex items-center space-x-2 px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              onClick={() => setIsMobileSidebarOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
              <span>Menu</span>
            </button>
          </div>

          {/* Mobile Sidebar Overlay */}
          {isMobileSidebarOpen && (
            <div className="md:hidden fixed inset-0 z-50 flex">
              <div
                className="fixed inset-0 bg-black bg-opacity-50"
                onClick={() => setIsMobileSidebarOpen(false)}
              ></div>
              <div className="relative ml-auto w-72 max-w-[80%] bg-white h-full shadow-lg">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Navigate</h2>
                  <button
                    onClick={() => setIsMobileSidebarOpen(false)}
                    className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    aria-label="Close menu"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      Backlog
                    </h3>
                    <button
                      className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-100"
                      onClick={() => {
                        setIsMobileSidebarOpen(false);
                        goToDashboard("backlog");
                      }}
                    >
                      Backlog
                    </button>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      Sprints
                    </h3>
                    <div className="space-y-1">
                      {sprints.map((s: any) => (
                        <button
                          key={s.id}
                          className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-100"
                          onClick={() => {
                            setIsMobileSidebarOpen(false);
                            goToDashboard(String(s.id));
                          }}
                          title={s.description}
                        >
                          {s.name}
                          {s.status ? (
                            <span className="ml-2 text-xs text-gray-500">
                              ({s.status})
                            </span>
                          ) : null}
                        </button>
                      ))}
                      {sprints.length === 0 && (
                        <div className="text-xs text-gray-400 px-3">
                          No sprints
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="max-w-5xl mx-auto">
            {/* Context chips: Team and Sprint */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {selectedTeam && (
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs sm:text-sm font-medium rounded-full">
                  Team: {selectedTeam.name}
                </span>
              )}
              {sprintForTask && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs sm:text-sm font-medium rounded-full inline-flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{sprintForTask.name}</span>
                  <span className="text-blue-700/80">
                    ({formatDate(sprintForTask.startDate)} –{" "}
                    {formatDate(sprintForTask.endDate)})
                  </span>
                  {sprintForTask.status && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/70 text-blue-700 text-[10px] sm:text-xs">
                      {sprintForTask.status}
                    </span>
                  )}
                </span>
              )}
              {!sprintForTask && task?.status === "backlog" && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs sm:text-sm font-medium rounded-full">
                  In Backlog
                </span>
              )}
            </div>
            <StoryModal
              task={task}
              isOpen={true}
              onClose={handleClose}
              onUpdate={(updated) => setTask(updated)}
              sprints={sprints}
              embedded={true}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default TaskPage;
